import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ActivityIndicator, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { colors } from '@chillout/core'
import { Card } from '../../shared/Card'
import { SeriesPill } from './SeriesPill'
import { TotalMembersBadge } from './TotalMembersBadge'
import type { TrendDataPoint } from './chartTypes'

export type { TrendDataPoint }

interface LoyaltyTrendsChartProps {
  data: TrendDataPoint[]
  dateFrom: Date
  dateTo: Date
  loading?: boolean
  error?: boolean
  totalMembers: number
  totalMembersLoading?: boolean
}

function formatXTick(dateStr: string, totalDays: number): string {
  const date = parseISO(dateStr)
  return totalDays <= 7 ? format(date, 'EEE') : format(date, 'MMM d')
}

export function LoyaltyTrendsChart({ data, dateFrom, dateTo, loading, error, totalMembers, totalMembersLoading }: LoyaltyTrendsChartProps) {
  const { t } = useTranslation()

  // The separator between member and chips only makes sense when they're
  // genuinely on the same line — title/member/chips only fit on one line
  // above some width that depends on translated chip label lengths and how
  // many series exist, so it can't be a fixed breakpoint. Measure both
  // elements' actual position directly (rather than trusting onLayout, which
  // in RN Web only fires when an element's own size changes, not when it
  // merely repositions because a sibling resized or the window narrowed) —
  // and compare vertical centers, not top edges, since items-center aligns
  // items of different heights by center, not top.
  const memberElRef = useRef<View>(null)
  const chipsElRef = useRef<View>(null)
  const [memberAndChipsSameRow, setMemberAndChipsSameRow] = useState(false)

  useLayoutEffect(() => {
    function recomputeSameRow() {
      const memberEl = memberElRef.current as unknown as HTMLElement | null
      const chipsEl = chipsElRef.current as unknown as HTMLElement | null
      if (!memberEl || !chipsEl) return
      const memberRect = memberEl.getBoundingClientRect()
      const chipsRect = chipsEl.getBoundingClientRect()
      const memberCenter = memberRect.top + memberRect.height / 2
      const chipsCenter = chipsRect.top + chipsRect.height / 2
      setMemberAndChipsSameRow(Math.abs(memberCenter - chipsCenter) < 2)
    }

    recomputeSameRow()
    window.addEventListener('resize', recomputeSameRow)
    return () => window.removeEventListener('resize', recomputeSameRow)
  }, [totalMembers, totalMembersLoading])

  const series = useMemo(
    () => [
      {
        key: 'stampsAdded' as const,
        label: t('adminDashboard.home.stats.stampsAdded'),
        color: colors.accent.DEFAULT,
      },
      {
        key: 'rewardsRedeemed' as const,
        label: t('adminDashboard.home.stats.rewardsRedeemed'),
        color: colors.warning,
      },
      {
        key: 'rewardsUnlocked' as const,
        label: t('adminDashboard.home.stats.rewardsUnlocked'),
        color: colors.success,
      },
      {
        key: 'newMembers' as const,
        label: t('adminDashboard.home.stats.newMembers'),
        color: colors.role.staff.text,
      },
    ],
    [t],
  )

  const [activeSeries, setActiveSeries] = useState<string>('stampsAdded')

  const totalDays = differenceInCalendarDays(dateTo, dateFrom) + 1
  const isBarChart = totalDays <= 7

  const active = series.find((s) => s.key === activeSeries) ?? series[0]

  const xInterval = isBarChart ? 0 : Math.max(0, Math.floor(totalDays / 6) - 1)

  const axisTickStyle = {
    fontSize: 11,
    fill: colors.text.muted,
  }

  const tooltipContentStyle = {
    backgroundColor: colors.canvas.card,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px',
  }

  const sharedChartProps = {
    data,
    margin: { top: 4, right: 4, left: -20, bottom: 0 },
  }

  const grid = (
    <CartesianGrid strokeDasharray="3 3" stroke={colors.border} vertical={false} />
  )

  const xAxis = (
    <XAxis
      dataKey="date"
      tickFormatter={(v: string) => formatXTick(v, totalDays)}
      tick={axisTickStyle}
      tickLine={false}
      axisLine={false}
      interval={xInterval}
    />
  )

  const yAxis = (
    <YAxis
      tick={axisTickStyle}
      tickLine={false}
      axisLine={false}
      allowDecimals={false}
    />
  )

  const tooltip = (
    <Tooltip
      contentStyle={tooltipContentStyle}
      labelStyle={{ color: colors.text.secondary, marginBottom: 4, fontWeight: 600 }}
      labelFormatter={(label) => format(parseISO(label as string), 'd MMM yyyy')}
      formatter={(value) => [value, active.label]}
      cursor={isBarChart ? { fill: `${colors.border}80` } : { stroke: colors.border }}
    />
  )

  return (
    <Card variant="dashboard" className="p-4 lg:p-6">
      <View className="mb-4 flex-row flex-wrap items-center justify-start gap-3 lg:justify-end">
        <View className="shrink-0">
          <Text className="text-base font-semibold text-primary">
            {t('adminDashboard.home.chart.title')}
          </Text>
          <Text className="mt-0.5 text-sm text-secondary">
            {t('adminDashboard.home.chart.subtitle')}
          </Text>
        </View>

        {/* Title, member, and chips are independent flex-wrap items, in that
            order, so the row collapses naturally as space runs out: all
            three sit on one line when there's room; below that, title+member
            share a line (member pushed to the right by the spacer) with
            chips wrapping to its own line below; on mobile the spacer
            disappears and member goes full-width, forcing each onto its own
            line. Chips itself always wraps internally so its pills never
            overflow whichever line they land on. */}
        <View className="hidden lg:flex lg:min-w-3 lg:flex-1" />

        <View ref={memberElRef} className="w-full lg:w-auto lg:shrink-0">
          <TotalMembersBadge value={totalMembers} loading={totalMembersLoading} />
        </View>
        {memberAndChipsSameRow && <View className="hidden w-px self-stretch bg-border lg:flex" />}
        <View
          ref={chipsElRef}
          className="w-full flex-row flex-wrap justify-end gap-2 lg:w-auto lg:max-w-full lg:shrink-0"
        >
          {series.map((s) => (
            <SeriesPill
              key={s.key}
              label={s.label}
              color={s.color}
              active={activeSeries === s.key}
              onPress={() => setActiveSeries(s.key)}
            />
          ))}
        </View>
      </View>

      {loading || error || data.length === 0 ? (
        <View className="h-64 items-center justify-center">
          {loading
            ? <ActivityIndicator size="small" color={colors.text.muted} />
            : <Text className="text-sm text-muted">{error ? t('common.errorLoadingData') : t('common.noData')}</Text>
          }
        </View>
      ) : (
        <View style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            {isBarChart ? (
              <BarChart {...sharedChartProps}>
                {grid}
                {xAxis}
                {yAxis}
                {tooltip}
                <Bar
                  dataKey={active.key}
                  fill={active.color}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={52}
                />
              </BarChart>
            ) : (
              <LineChart {...sharedChartProps}>
                {grid}
                {xAxis}
                {yAxis}
                {tooltip}
                <Line
                  type="monotone"
                  dataKey={active.key}
                  stroke={active.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </View>
      )}
    </Card>
  )
}